import { Box, CircularProgress, FormGroup, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import DropzoneFieldV2 from "components/FormFields/DropzoneFieldV2";
import FormDropdown from "components/FormFields/FormDropdown";
import FormInputV2 from "components/FormFields/FormInputV2";
import FormMaskedInput from "components/FormFields/FormMaskedInput";
import PreviewData from "components/FormFields/PreviewData";
import FormDatePicker from "components/Forms/FormDatePicker";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import PreviewDocument from "components/PreviewDocument";
import PreviewObservations from "components/PreviewObservations";
import { deleteContractApprovalWorkCenterEmployee, getContract, getContractApprovalCertificate, getContractApprovalWorkCenterEmployeeById, updateContractApprovalWorkCenterEmployee } from "crud/contractApprovalWorkCenterEmployee.crud";
import { getEmployeeDocumentById } from "crud/workCentersEmployer.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { useDataset } from "hooks/useDataset";
import usePreviewDocument from "hooks/usePreviewDocument";
import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { listContractApprovalWorkCenterEmployeeAction } from "redux/actions/contractApprovalWorkCenterEmployeeAction";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { errorMessages } from "resources/resources";
import { DIALOG } from "utils/actionsConsts";
import { COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID, CONTRACT_APPROVAL_EMPLOYEE_STATUS, CONTRACT_APPROVAL_STATUS, CONTRACT_TYPES_KEYVALUES, DATASET, DOCUMENT_PDF_TYPE, INPUT_MASKS } from "utils/const";
import { getKeyValueByCode } from "utils/datasets";
import { jsonToFormData } from "utils/formData";

const useStylesLocal = makeStyles((theme) => ({
  dialogHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
    },
  },
  headerButton: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  documentsRow: {
    gap: "2rem",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
  footerButtons: {
    gap: "1rem",
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
}));

const ContractApprovalWorkerDetailsDialog = ({
  openDialog,
  handleCloseDetailsDialog,
  workerId,
  isDraft,
  requestNumber,
  stateKeyValue,
}) => {
  const localClasses = useStylesLocal();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const [{ state }, handleDownload] = usePreviewDocument(getContractApprovalCertificate);

  const { new_contract_approval } = useSelector((state) => state.contractApprovalState) || {};

  const methods = useForm({
    defaultValues: {},
    shouldFocusError: false,
  });

  const { data: PROVINCES } = useDataset({ name: DATASET.PROVINCES });

  const provinceId = methods.watch("provinceId");
  const provinceKeyValue = useMemo(() => getKeyValueByCode(PROVINCES, provinceId), [PROVINCES, provinceId]);

  const { data: MUNICIPALITIES } = useDataset(
    { name: DATASET.MUNICIPALITIES, parentKeyValue: provinceKeyValue },
    {
      enabled: !!provinceKeyValue,
    }
  );

  const { data: MARITAL_STATUS } = useDataset({ name: DATASET.MARITAL_STATUS });
  const { data: GENDER } = useDataset({ name: DATASET.GENDER });
  const { data: ACADEMIC_LEVEL } = useDataset({ name: DATASET.ACADEMIC_LEVEL });
  const { data: PROFESSIONAL_CATEGORY } = useDataset({ name: DATASET.PROFESSIONAL_CATEGORY });
  const { data: NATIONALITIES } = useDataset({ name: DATASET.NATIONALITIES });
  const { data: CONTRACT_TYPE } = useDataset({ name: DATASET.CONTRACT_TYPE });
  const { data: COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES } = useDataset({ name: DATASET.COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES });
  const { data: COMPANY_ACTIVITY } = useDataset({ name: DATASET.COMPANY_ACTIVITY });

  const queryClient = useQueryClient();

  const { isLoading: isLoadingEmployeeInfo, data } = useQuery(
    ["getWorkCenterEmployerById"],
    () => getContractApprovalWorkCenterEmployeeById(workerId),
    {
      cacheTime: 0,
      onSuccess: ({ data }) => {
        methods.reset({
          ..._.omit(data),
          name: data?.workCenterEmployee?.name,
          ssn: data?.workCenterEmployee?.ssn,
          nif: data?.workCenterEmployee?.nif,
          address: data?.workCenterEmployee?.address,
          provinceId: data?.workCenterEmployee?.provinceId,
          municipalityId: data?.workCenterEmployee?.municipalityId,
          nationalityId: data?.workCenterEmployee?.nationalityId,
          dateOfBirth: moment(data?.workCenterEmployee?.dateOfBirth),
          genderId: data?.workCenterEmployee?.genderId,
          maritalStatusId: data?.workCenterEmployee?.maritalStatusId,
          academicLevelId: data?.workCenterEmployee?.academicLevelId,
          primaryFunctionId: data?.workCenterEmployee?.primaryFunctionId,
          documentTypeId: data?.workCenterEmployee?.documentTypeId,
          identityDocumentNumber: data?.workCenterEmployee?.identityDocumentNumber,
          identityDocumentIssueDate: moment(data?.workCenterEmployee?.identityDocumentIssueDate),
          identityDocumentExpiryDate: moment(data?.workCenterEmployee?.identityDocumentExpiryDate),
          mainCompanyActivity: data?.workCenterEmployee?.mainCompanyActivity,
          contractType: data?.contractTypeId,
          contractStartDate: moment(data?.contractStartDate),
          contractEndDate: moment(data?.contractEndDate),
          baseSalary: data?.workCenterEmployee?.baseSalary,
          salaryStatedInTheContract: data?.workCenterEmployee?.salaryStatedInTheContract,
          documents: [],
          workVisaNumber: data?.workCenterEmployee?.workVisaNumber,
        });
      },
    }
  );

  const removeWorker = async () => {
    if (!workerId) {
      throw new Error("O id do trabalhador não está definido.");
    }
    await removeWorkerAsync(workerId);
    await dispatch(listContractApprovalWorkCenterEmployeeAction(new_contract_approval?.requestNumber || requestNumber));
    handleCloseDetailsDialog();
  };

  const { mutateAsync: removeWorkerAsync } = useMutation({
    mutationFn: async () => {
      if (!workerId) {
        throw new Error("O id do trabalhador não está definido.");
      }
      return await deleteContractApprovalWorkCenterEmployee(workerId);
    },
    mutationKey: ["deleteContractApprovalWorkCenterEmployee"],
    onSuccess: async () => {
      await dispatch({
        type: DIALOG.SHOW,
        payload: {
          showGenericDialog: true,
          genericDialogData: {
            type: DIALOG.ONE_BUTTON_TYPE,
            isSuccess: true,
            buttonLabel: "Ok",
            message: "Trabalhador removido com sucesso.",
          },
        },
      });

      queryClient.invalidateQueries(["listEmployee", new_contract_approval?.requestNumber || requestNumber]);
    },
    onError: (error) => {
      console.error("Mutation failed", error);
    },
  });

  const handleEditSuccess = async () => {
    setIsEditing(false);
    await dispatch(listContractApprovalWorkCenterEmployeeAction(new_contract_approval?.requestNumber || requestNumber));
    handleCloseDetailsDialog();
    await queryClient.refetchQueries({
      queryKey: ["listEmployee", new_contract_approval?.requestNumber || requestNumber],
      exact: true,
    });

    await queryClient.refetchQueries({
      queryKey: ["contractApprovalDetails", new_contract_approval?.requestNumber || requestNumber],
      exact: true,
    });
  };

  const { mutateAsync: updateWorkerAsync } = useMutation({
    mutationFn: async () => {
      if (!workerId) {
        throw new Error("O id do trabalhador não está definido.");
      }
      const contractDetails = {
        contractTypeId: methods.getValues("contractType"),
        contractStartDate: moment(methods.getValues("contractStartDate")).toISOString(),
        contractEndDate: moment(methods.getValues("contractEndDate")).toISOString(),
        contractDocument: methods.getValues("workerContract") || null,
      };
      return await updateContractApprovalWorkCenterEmployee(
        jsonToFormData({
          Id: workerId,
          Worker: contractDetails,
          ContractApprovalRequestId: new_contract_approval?.requestNumber || requestNumber,
        })
      );
    },
    mutationKey: ["updateContractApprovalWorkCenterEmployee"],
    onSuccess: () => {
      dispatch({
        type: DIALOG.SHOW,
        payload: {
          showGenericDialog: true,
          genericDialogData: {
            type: DIALOG.ONE_BUTTON_TYPE,
            isSuccess: true,
            buttonLabel: "Ok",
            message:
              workerData?.contractApprovalWorkCenterEmployeeStatusKeyValue === CONTRACT_APPROVAL_EMPLOYEE_STATUS.DENIED
                ? "Correcções efectuadas com sucesso."
                : "Trabalhador editado com sucesso.",
            okAction: handleEditSuccess,
          },
        },
      });
      queryClient.invalidateQueries(["listEmployee", requestNumber]);
      queryClient.invalidateQueries(["contractApprovalDetails", requestNumber]);
    },
    onError: (error) => {
      console.error("Mutation failed", error);
    },
  });

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const confirmationUpdateWorker = async () => {
    showDialog(
      "Actualizar Trabalhador",
      "Tem a certeza que pretende actualizar este trabalhador?",
      async () => {
        await updateWorkerAsync();
      },
      () => {}
    );
  };

  const confirmationRemoveWorker = async () => {
    showDialog(
      "Remover Trabalhador",
      "Tem a certeza que pretende remover o trabalhador do pedido?",
      async () => {
        await removeWorker();
      },
      () => {}
    );
  };


  const workerData = data?.data;

  const workerContract = [
    {
      documentId: workerData?.id,
      documentName: "Contrato de Trabalho",
      documentTypeId: workerData?.contractTypeId,
    },
  ];

  const getDocumentName = (documentTypeId) => {
    switch (documentTypeId) {
      case COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.PASSPORT:
        return "Passaporte";
      case COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.VISA:
        return "Visto de Trabalho";
      case COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.RESIDENT_CARD:
        return "Cartão de Residente";
      case COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.IDENTITY_CARD:
        return "Bilhete de Identidade";
      default:
        return "";
    }
  };

  const workerDocuments = workerData?.workCenterEmployee?.documents?.map((document) => ({
    documentId: document.documentId,
    documentName: getDocumentName(document.documentTypeId),
    documentTypeId: document.documentTypeId,
  }));

  useEffect(() => {
    if (
      workerData?.contractApprovalWorkCenterEmployeeStatusKeyValue === CONTRACT_APPROVAL_EMPLOYEE_STATUS.DENIED &&
      stateKeyValue !== CONTRACT_APPROVAL_STATUS.CONCLUDED
    ) {
      setIsEditing(true);
    }
  }, [workerData, stateKeyValue]);

  const contractType = methods.watch("contractType");

  const isIndefiniteContract = useMemo(() => {
    const contratoIndeterminadoKeyValue = getKeyValueByCode(CONTRACT_TYPE, contractType);
    return (
      contratoIndeterminadoKeyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_INDETERMINADO_NACIONAL ||
      contratoIndeterminadoKeyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_INDETERMINADO_ESTRANGEIROS_RESIDENTES
    );
  }, [CONTRACT_TYPE, contractType]);

  useEffect(() => {
    if (isIndefiniteContract) {
      methods.setValue("contractEndDate", null);
    }
  }, [isIndefiniteContract, methods]);

  const nationalityId = methods.watch("nationalityId");

  const angola = NATIONALITIES?.find((n) => n.keyValue === "Nationalities_06");

  const isAngola = nationalityId === (angola?.code ?? null);

  const FilteredContractTypes = useMemo(() => {
    return CONTRACT_TYPE?.filter((item) => {
      if (isAngola) {
        return (
          item.keyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_DETERMINADO_NACIONAL ||
          item.keyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_INDETERMINADO_NACIONAL
        );
      } else {
        return (
          item.keyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_DETERMINADO_ESTRANGEIROS_NAO_RESIDENTES ||
          item.keyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_DETERMINADO_ESTRANGEIROS_RESIDENTES ||
          item.keyValue === CONTRACT_TYPES_KEYVALUES.TEMPO_INDETERMINADO_ESTRANGEIROS_RESIDENTES
        );
      }
    }).map((item) => ({ code: item?.code, label: item?.label, keyValue: item?.keyValue }));
  }, [CONTRACT_TYPE, isAngola]);

  return (
    <FormProvider {...methods}>
      <form>
        <GenericDialog open={openDialog} onClose={handleCloseDetailsDialog} maxWidth="lg">
          <Box className={localClasses.dialogHeader}>
            <PageTitleHeader
              title="Dados do Trabalhador"
              small={isSmDown}
              style={
                isSmDown
                  ? undefined
                  : {
                      marginLeft: "-22px",
                    }
              }
            />
            <ConditionGuard condition={isDraft}>
              <GenericButton
                typeSubmit={false}
                size="medium"
                color="tertiary"
                onClick={() => setIsEditing(!isEditing)}
                className={localClasses.headerButton}
                fullWidth={isSmDown}
              >
                {isEditing ? "Cancelar" : "Editar"}
              </GenericButton>
            </ConditionGuard>
          </Box>
          <ConditionGuard
            condition={!isLoadingEmployeeInfo}
            fallback={
              <Box display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Box>
            }
          >
            <Grid container>
              <ConditionGuard condition={workerData?.contractApprovalWorkCenterEmployeeStatusKeyValue === "Denied"}>
                <Grid item xs={12} style={{ marginBottom: "1rem" }}>
                  <Typography variant="h2" color="primary">
                    Motivo de Rejeição
                  </Typography>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewData title="Motivo" subtitle={workerData?.rejectMotive} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewData title="Descrição" subtitle={workerData?.rejectMotiveDescription} />
                  </Grid>
                </Grid>
              </ConditionGuard>
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Dados Pessoais
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInputV2
                  name="name"
                  label="Nome do Trabalhador"
                  placeholder="Insira o nome do trabalhador"
                  required
                  control={methods.control}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="dateOfBirth"
                  label="Data de Nascimento"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  isRequired={true}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Género"
                  placeholder="Seleccione o Género"
                  control={methods.control}
                  name="genderId"
                  options={GENDER}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Estado Civil"
                  placeholder="Seleccione o estado civil"
                  control={methods.control}
                  name="maritalStatusId"
                  options={MARITAL_STATUS}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Nível Académico"
                  placeholder="Seleccione o nível académico"
                  control={methods.control}
                  name="academicLevelId"
                  options={ACADEMIC_LEVEL}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="nif"
                  label="NIF"
                  placeholder="Insira o NIF"
                  required
                  control={methods.control}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="ssn"
                  label="NISS"
                  placeholder="Insira o NISS"
                  required
                  control={methods.control}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  label="Nacionalidade"
                  control={methods.control}
                  name="nationalityId"
                  options={NATIONALITIES}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  control={methods.control}
                  label="Província"
                  name="provinceId"
                  options={PROVINCES}
                  required={isAngola}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  control={methods.control}
                  label="Município"
                  name="municipalityId"
                  options={MUNICIPALITIES}
                  required={isAngola}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInputV2
                  name="address"
                  label="Morada"
                  placeholder="Insira a morada do trabalhador"
                  required={isAngola}
                  control={methods.control}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Documento de Identificação"
                  placeholder="Seleccione o documento de identificação"
                  control={methods.control}
                  name="documentTypeId"
                  options={COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="identityDocumentNumber"
                  label="N.º do Documento de Identificação"
                  placeholder="Insira n.º de documento"
                  control={methods.control}
                  disabled={true}
                  rules={{
                    required: {
                      value: true,
                      message: intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="identityDocumentIssueDate"
                  label="Data de Emissão"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  endDatename="endDate"
                  isRequired={true}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="identityDocumentExpiryDate"
                  label="Data de Validade"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={false}
                  minDateToday={true}
                  endDatename="endDate"
                  isRequired={true}
                  disabled={true}
                />
              </Grid>
              <ConditionGuard condition={workerData?.workCenterEmployee?.contractTypeId === 5}>
                <Grid item xs={12} md={3}>
                  <FormInputV2
                    name="workVisaNumber"
                    label="Nº Visto de Trabalho"
                    placeholder="Nº Visto de Trabalho"
                    required
                    control={methods.control}
                    disabled={true}
                  />
                </Grid>
              </ConditionGuard>
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Informações Sobre a Actividade
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Actividade Principal da Empresa"
                  placeholder="Actividade Principal da Empresa"
                  control={methods.control}
                  name="mainCompanyActivity"
                  options={COMPANY_ACTIVITY}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Profissão"
                  placeholder="Seleccione a profissão"
                  control={methods.control}
                  name="primaryFunctionId"
                  options={PROFESSIONAL_CATEGORY}
                  required
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Tipo de Contrato"
                  placeholder="Tipo de Contrato"
                  control={methods.control}
                  name="contractType"
                  options={FilteredContractTypes}
                  required
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="contractStartDate"
                  label="Data de Início de Contrato"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  isRequired={true}
                  disabled={!isEditing}
                />
              </Grid>
              <ConditionGuard condition={!isIndefiniteContract}>
                <Grid item xs={12} md={3}>
                  <FormDatePicker
                    name="contractEndDate"
                    label="Data de Fim de Contrato"
                    control={methods.control}
                    rules={{ required: false }}
                    getValues={methods.getValues}
                    setValue={methods.setValue}
                    trigger={methods.trigger}
                    maxDateToday={false}
                    minDateToday={true}
                    isRequired={!isIndefiniteContract}
                    disabled={isIndefiniteContract || !isEditing}
                  />
                </Grid>
              </ConditionGuard>
              <Grid item xs={12} md={3}>
                <FormMaskedInput
                  name="salaryStatedInTheContract"
                  label="Salário Expresso no Contrato"
                  placeholder="Insira o salário expresso no contrato"
                  required
                  control={methods.control}
                  mask={INPUT_MASKS.MONETARY}
                  disabled={true}
                  rules={{
                    validate: {
                      minimumSalary: (value) => {
                        const numericValue = parseInt(value.replace(/[^0-9]/g, ""));
                        if (numericValue < 50000) {
                          return "Valor deverá ser igual ou superior ao salário mínimo";
                        }
                        return true;
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormMaskedInput
                  name="baseSalary"
                  label="Remuneração Total"
                  placeholder="Insira a remuneração total"
                  required
                  control={methods.control}
                  mask={INPUT_MASKS.MONETARY}
                  disabled={true}
                  rules={{
                    validate: {
                      minimumSalary: (value) => {
                        const numericValue = parseInt(value.replace(/[^0-9]/g, ""));
                        if (numericValue < 50000) {
                          return "Valor deverá ser igual ou superior ao salário mínimo";
                        }
                        return true;
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Documentos
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
                className={localClasses.documentsRow}
              >
                <ConditionGuard condition={isEditing}>
                  <Grid item xs={12} md={3}>
                    <FormGroup>
                      <DropzoneFieldV2
                        name={"workerContract"}
                        inputLabel={"Substituir documento"}
                        label={"Contrato de trabalho"}
                        maxSize={2}
                        required={false}
                        accept={DOCUMENT_PDF_TYPE}
                      />
                    </FormGroup>
                  </Grid>
                </ConditionGuard>
                <ConditionGuard condition={!isEditing}>
                  <PreviewDocument title="Contrato de Trabalho" documents={workerContract} promise={getContract} />
                </ConditionGuard>
                <ConditionGuard condition={workerDocuments?.length > 0}>
                  <PreviewDocument
                    title="Documento de Identificação"
                    documents={workerDocuments}
                    promise={getEmployeeDocumentById}
                  />
                </ConditionGuard>
              </Grid>
              <ConditionGuard condition={!isDraft}>
                <Grid item xs={12} md={12}>
                  <Typography variant="h2" style={{ color: "#002e9e", marginTop: "1rem" }}>
                    Observações
                  </Typography>
                </Grid>
                <PreviewObservations observations={workerData?.observations} />
              </ConditionGuard>
              <Grid
                item
                xs={12}
                md={12}
                className={localClasses.footerButtons}
              >
                <GenericButton
                  typeSubmit={false}
                  size="medium"
                  color="tertiary"
                  onClick={handleCloseDetailsDialog}
                  fullWidth={isSmDown}
                >
                  Fechar
                </GenericButton>
                <ConditionGuard condition={isDraft && !isEditing}>
                  <GenericButton
                    typeSubmit={false}
                    size="medium"
                    color="tertiaryRed"
                    onClick={confirmationRemoveWorker}
                    fullWidth={isSmDown}
                  >
                    Remover Trabalhador
                  </GenericButton>
                </ConditionGuard>
                <ConditionGuard condition={workerData?.contractApprovalWorkCenterEmployeeStatusID === 2}>
                  <GenericButton
                    size="medium"
                    color="primary"
                    onClick={methods.handleSubmit(() => handleDownload(workerData?.certificateNumber))}
                    loading={methods.formState.isSubmitting || state.get(workerData?.certificateNumber)}
                    fullWidth={isSmDown}
                  >
                    Exportar Certificado
                  </GenericButton>
                </ConditionGuard>
                <ConditionGuard condition={isEditing}>
                  <GenericButton
                    size="medium"
                    color="primary"
                    onClick={methods.handleSubmit(confirmationUpdateWorker)}
                    loading={methods.formState.isSubmitting}
                    fullWidth={isSmDown}
                  >
                    {workerData?.contractApprovalWorkCenterEmployeeStatusID === 3 ? "Guardar Correcções" : "Guardar"}
                  </GenericButton>
                </ConditionGuard>
              </Grid>
            </Grid>
          </ConditionGuard>
        </GenericDialog>
      </form>
    </FormProvider>
  );
};

export default ContractApprovalWorkerDetailsDialog;
